import React, { useId, useState } from 'react'
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Paper, TextField, Typography } from '@mui/material'

import { RECON_PLATFORMS, platformLabel, reconQueryProblem, type ReconQuery } from '../reconciliationModel'

/**
 * The on-demand Reconciliation page's controls (design/2026-10-19-sky-reconciliation-on-demand.md):
 * two UTC dates, the four platforms Platinum imports, and Apply. A Manager's
 * "Read Sprinklr again" sits on the provenance line, beside when Sprinklr was
 * read (T19b).
 *
 * The controls hold a draft; nothing is asked of Sprinklr until Apply. The
 * parent remounts this with a `key` when the applied query changes (a link,
 * Back), so the draft always starts from what is on screen.
 */
export interface RangeControlsProps {
  query: ReconQuery
  onApply: (q: ReconQuery) => void
  /** A read is running: Apply waits. */
  busy?: boolean
}

const RangeControls: React.FC<RangeControlsProps> = ({ query, onApply, busy }) => {
  const [from, setFrom] = useState(query.from)
  const [to, setTo] = useState(query.to)
  const [platforms, setPlatforms] = useState<string[]>(query.platforms)
  const problemId = useId()

  const draft: ReconQuery = { from, to, platforms: RECON_PLATFORMS.filter((p) => platforms.includes(p)) }
  const problem = reconQueryProblem(draft)

  const toggle = (p: string, on: boolean) => setPlatforms((cur) => (on ? [...cur, p] : cur.filter((x) => x !== p)))

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="recon-range">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <TextField
          label="From (UTC)"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="To (UTC)"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <FormGroup row aria-label="Platforms">
          {RECON_PLATFORMS.map((p) => (
            <FormControlLabel
              key={p}
              label={platformLabel(p)}
              control={<Checkbox color="info" size="small" checked={platforms.includes(p)} onChange={(_, v) => toggle(p, v)} />}
            />
          ))}
        </FormGroup>
        <Box sx={{ display: 'flex', gap: 1, ml: { md: 'auto' } }}>
          <Button
            variant="contained"
            color="info"
            disabled={Boolean(problem) || busy}
            aria-describedby={problem ? problemId : undefined}
            onClick={() => onApply(draft)}
          >
            Apply
          </Button>
        </Box>
      </Box>
      {problem ? (
        <Typography id={problemId} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {problem}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Days are UTC days, both dates included. A new range asks Sprinklr and can take up to two minutes.
        </Typography>
      )}
    </Paper>
  )
}

export default RangeControls
